// src/components/sku/SkuListTable.tsx
import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import type { SkuItem } from "src/types/sku";

type Props = {
  list: SkuItem[];
  onEdit?: (item: SkuItem) => void;
};

export default function SkuListTable({ list, onEdit }: Props) {
  return (
    // ✅ 화면이 900px보다 작아지면 여기에서 가로 스크롤이 생김
    <Box sx={{ width: "100%", overflowX: "auto", minWidth: 900 }}>
      {/* ✅ 900~1300 사이에서만 폭이 변하고, 1300 이상이면 중앙 고정 */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          width: "clamp(900px, 100%, 1300px)",
          mx: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 900,
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 900, width: 80, whiteSpace: "nowrap" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 900, width: 140, whiteSpace: "nowrap" }}>
                skuCode
              </TableCell>
              <TableCell sx={{ fontWeight: 900, width: 160, whiteSpace: "nowrap" }}>
                skuName
              </TableCell>
              <TableCell sx={{ fontWeight: 900, width: 120, whiteSpace: "nowrap" }}>
                skuType
              </TableCell>
              <TableCell sx={{ fontWeight: 900, width: 120, whiteSpace: "nowrap" }}>
                isActive
              </TableCell>
              <TableCell sx={{ fontWeight: 900, width: 180, whiteSpace: "nowrap" }}>
                updatedAt
              </TableCell>
              <TableCell sx={{ fontWeight: 900, width: 120, whiteSpace: "nowrap" }} align="right">
                actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4 }}>
                  <Stack alignItems="center">
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      데이터가 없습니다.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              list.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{it.id}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{it.skuCode}</TableCell>

                  {/* ✅ 이 칸만 줄바꿈 허용(설명 포함) */}
                  <TableCell sx={{ whiteSpace: "normal" }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{it.skuName}</Typography>
                    {it.skuDescription ? (
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {it.skuDescription}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>{it.skuType}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      size="small"
                      label={it.isActive ? "ACTIVE" : "INACTIVE"}
                      variant={it.isActive ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(it.updatedAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => onEdit?.(it)} disabled={!onEdit}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
